import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Palette, Loader2, Trash2, Shirt, Smartphone, Sticker, Image,
  Wand2, Upload, ShoppingCart, ArrowRight, Check, Coffee, FileImage,
  Sparkles, X, Laptop, Backpack, Flag
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import { useAuth, useCart } from "../App";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRODUCT_TYPES = [
  { id: "t-shirt", label: "T-Shirt", icon: Shirt, mockupColor: "#1a1a1a" },
  { id: "hoodie", label: "Hoodie", icon: Shirt, mockupColor: "#111111" },
  { id: "tank-top", label: "Tank Top", icon: Shirt, mockupColor: "#181818" },
  { id: "joggers", label: "Joggers", icon: Shirt, mockupColor: "#131313" },
  { id: "snapback", label: "Snapback", icon: Shirt, mockupColor: "#151515" },
  { id: "beanie", label: "Beanie", icon: Shirt, mockupColor: "#141414" },
  { id: "phone-case", label: "Phone Case", icon: Smartphone, mockupColor: "#0a0a0a" },
  { id: "sticker", label: "Sticker", icon: Sticker, mockupColor: "#151515" },
  { id: "mug", label: "Mug", icon: Coffee, mockupColor: "#0d0d0d" },
  { id: "poster", label: "Poster", icon: FileImage, mockupColor: "#080808" },
  { id: "tote-bag", label: "Tote Bag", icon: ShoppingCart, mockupColor: "#121212" },
  { id: "laptop-sleeve", label: "Laptop Sleeve", icon: Laptop, mockupColor: "#0e0e0e" },
  { id: "backpack", label: "Backpack", icon: Backpack, mockupColor: "#101010" },
  { id: "socks", label: "Socks", icon: Shirt, mockupColor: "#161616" },
  { id: "desk-pad", label: "Desk Pad", icon: Laptop, mockupColor: "#0c0c0c" },
  { id: "blanket", label: "Blanket", icon: Shirt, mockupColor: "#0b0b0b" },
  { id: "flag", label: "Flag", icon: Flag, mockupColor: "#090909" },
];

const STYLE_TEMPLATES = [
  { id: "", name: "Custom", desc: "Describe your own vision" },
  { id: "streetwear", name: "Streetwear", desc: "Bold urban typography" },
  { id: "neon_tokyo", name: "Neon Tokyo", desc: "Neon cityscape vibes" },
  { id: "car_culture", name: "Car Culture", desc: "JDM & racing graphics" },
  { id: "cyberpunk", name: "Cyberpunk", desc: "Futuristic circuits & glow" },
  { id: "japanese", name: "Japanese", desc: "Kanji & cherry blossoms" },
  { id: "retro", name: "Retro Wave", desc: "80s synthwave aesthetics" },
  { id: "minimal", name: "Minimal", desc: "Clean & sophisticated" },
  { id: "graffiti", name: "Graffiti", desc: "Spray paint art" },
];

// Step definitions
const STEPS = ["product", "design", "preview"];

export default function MerchStudioPage() {
  const { user } = useAuth();
  const { addToCart } = useCart();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [step, setStep] = useState(0);
  const [productType, setProductType] = useState("t-shirt");
  const [productOptions, setProductOptions] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [prompt, setPrompt] = useState("");
  const [generating, setGenerating] = useState(false);
  const [designs, setDesigns] = useState([]);
  const [selectedDesign, setSelectedDesign] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [customName, setCustomName] = useState("");
  const [selectedSize, setSelectedSize] = useState("");
  const [selectedColor, setSelectedColor] = useState("Black");
  const [creating, setCreating] = useState(false);
  const [createdProduct, setCreatedProduct] = useState(null);

  const token = localStorage.getItem("token");
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    if (!user) return;
    const load = async () => {
      try {
        const [optionsRes, designsRes] = await Promise.all([
          axios.get(`${API}/merch-studio/product-options`),
          axios.get(`${API}/merch-studio/my-designs`, { headers })
        ]);
        setProductOptions(optionsRes.data);
        setDesigns(designsRes.data);
      } catch (err) {
        console.error("Load error:", err);
      }
    };
    load();
  }, [user]);

  const currentProductInfo = productOptions.find(p => p.id === productType);

  const handleGenerate = async () => {
    if (!token) { toast.error("Please sign in first"); return; }
    if (!prompt.trim() && !selectedTemplate) { toast.error("Enter a description or pick a style"); return; }
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/merch-studio/generate`, {
        prompt, template_id: selectedTemplate, product_type: productType
      }, { headers });
      if (res.data.success) {
        setDesigns(prev => [res.data, ...prev]);
        setSelectedDesign(res.data);
        toast.success("Design created!");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Generation failed. Try again.");
    } finally { setGenerating(false); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please upload an image file"); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("File too large (max 10MB)"); return; }

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("product_type", productType);

    try {
      const res = await axios.post(`${API}/merch-studio/upload-design`, formData, {
        headers: { ...headers, "Content-Type": "multipart/form-data" }
      });
      if (res.data.success) {
        setDesigns(prev => [res.data, ...prev]);
        setSelectedDesign(res.data);
        toast.success("Design uploaded!");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const handleCreateProduct = async () => {
    if (!selectedDesign) { toast.error("Select a design first"); return; }
    if (!customName.trim()) { toast.error("Give your product a name"); return; }

    setCreating(true);
    try {
      const res = await axios.post(`${API}/merch-studio/create-custom-product`, {
        design_id: selectedDesign.design_id,
        product_type: productType,
        name: customName,
        size: selectedSize,
        color: selectedColor,
      }, { headers });
      if (res.data.success) {
        setCreatedProduct(res.data);
        toast.success("Custom product created!");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create product");
    } finally { setCreating(false); }
  };

  const handleAddToCart = async () => {
    if (!createdProduct) return;
    await addToCart({
      item_type: "merch",
      item_id: createdProduct.merch_id,
      size: selectedSize,
      color: selectedColor,
      quantity: 1
    });
    toast.success("Added to cart!");
  };

  const deleteDesign = async (id) => {
    try {
      await axios.delete(`${API}/merch-studio/designs/${id}`, { headers });
      setDesigns(prev => prev.filter(d => d.design_id !== id));
      if (selectedDesign?.design_id === id) setSelectedDesign(null);
      toast.success("Design deleted");
    } catch { toast.error("Failed to delete"); }
  };

  const canAdvance = () => {
    if (step === 0) return true;
    if (step === 1) return !!selectedDesign;
    return true;
  };

  // Login gate
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center py-20 px-4" data-testid="design-login-gate">
        <div className="glass border border-white/10 rounded-xl p-10 text-center max-w-md">
          <Palette className="w-12 h-12 text-neon-magenta mx-auto mb-4" />
          <h2 className="font-heading text-2xl uppercase mb-3">Design Your Own</h2>
          <p className="text-zinc-400 mb-6 text-sm">Sign in to create custom merchandise with AI-powered designs.</p>
          <Button onClick={() => navigate("/login")} className="bg-neon-magenta text-black font-bold uppercase" data-testid="login-to-design-btn">
            Sign In to Start
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-10 px-4 sm:px-6" data-testid="merch-studio-page">
      <div className="max-w-6xl mx-auto">

        {/* Header */}
        <div className="mb-8">
          <Badge className="bg-neon-magenta/20 text-neon-magenta border-neon-magenta/50 mb-4">
            Design Your Own
          </Badge>
          <h1 className="font-heading text-4xl sm:text-5xl uppercase tracking-tight mb-2">
            Custom <span className="text-neon-cyan">Merch</span> Studio
          </h1>
          <p className="text-zinc-400 text-sm sm:text-base max-w-xl">
            Upload your own design or let AI create one. We'll print and ship it worldwide via Printify.
          </p>
        </div>

        {/* Step Indicator */}
        <div className="flex items-center gap-2 mb-8">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center gap-2">
              <button
                onClick={() => { if (i <= step || (i === 1 && step >= 0) || (i === 2 && selectedDesign)) setStep(i); }}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs uppercase tracking-wide transition ${
                  i === step
                    ? "bg-neon-cyan/20 text-neon-cyan border border-neon-cyan/30"
                    : i < step
                      ? "bg-green-500/20 text-green-400 border border-green-500/30"
                      : "bg-zinc-800/50 text-zinc-500 border border-white/5"
                }`}
                data-testid={`step-${s}`}
              >
                {i < step ? <Check className="w-3 h-3" /> : <span className="w-4 text-center">{i + 1}</span>}
                {s === "product" ? "Choose Product" : s === "design" ? "Create Design" : "Preview & Order"}
              </button>
              {i < STEPS.length - 1 && <ArrowRight className="w-4 h-4 text-zinc-600" />}
            </div>
          ))}
        </div>

        {/* STEP 0: Choose Product */}
        <AnimatePresence mode="wait">
          {step === 0 && (
            <motion.div key="step0" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }}>
              <h2 className="font-heading text-base sm:text-lg uppercase tracking-wide text-zinc-300 mb-4">Choose Your Product</h2>
              <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 xl:grid-cols-9 gap-2 mb-8">
                {PRODUCT_TYPES.map(pt => {
                  const Icon = pt.icon;
                  const info = productOptions.find(p => p.id === pt.id);
                  return (
                    <button
                      key={pt.id}
                      onClick={() => setProductType(pt.id)}
                      className={`group relative flex flex-col items-center p-5 rounded-xl border transition-all ${
                        productType === pt.id
                          ? "border-neon-cyan/50 bg-neon-cyan/10 scale-[1.02]"
                          : "border-white/10 bg-zinc-900/50 hover:border-white/20"
                      }`}
                      data-testid={`product-type-${pt.id}`}
                    >
                      <Icon className={`w-8 h-8 mb-2 ${productType === pt.id ? "text-neon-cyan" : "text-zinc-400"}`} />
                      <span className="text-sm font-medium">{pt.label}</span>
                      {info && (
                        <span className="text-xs text-neon-magenta font-mono mt-1">
                          ${info.custom_price.toFixed(2)}
                        </span>
                      )}
                      {productType === pt.id && (
                        <div className="absolute -top-1 -right-1 w-5 h-5 bg-neon-cyan rounded-full flex items-center justify-center">
                          <Check className="w-3 h-3 text-black" />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Product details */}
              {currentProductInfo && (
                <div className="glass border border-white/10 rounded-xl p-5 mb-6">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-xs text-zinc-500 uppercase tracking-wide">Custom {PRODUCT_TYPES.find(p => p.id === productType)?.label}</p>
                      <p className="text-2xl font-mono text-neon-magenta font-bold mt-1">${currentProductInfo.custom_price.toFixed(2)}</p>
                      <p className="text-xs text-zinc-500 mt-1">(Base ${currentProductInfo.base_price.toFixed(2)} + $15 custom fee)</p>
                    </div>
                    <div className="text-right text-xs text-zinc-400">
                      {currentProductInfo.sizes?.length > 0 && <p>Sizes: {currentProductInfo.sizes.join(", ")}</p>}
                      {currentProductInfo.colors?.length > 0 && <p>Colors: {currentProductInfo.colors.join(", ")}</p>}
                    </div>
                  </div>
                </div>
              )}

              <Button
                onClick={() => setStep(1)}
                className="bg-neon-cyan text-black font-bold uppercase tracking-wider"
                data-testid="next-to-design-btn"
              >
                Next: Create Design <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </motion.div>
          )}

          {/* STEP 1: Create Design */}
          {step === 1 && (
            <motion.div key="step1" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }}>
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">

                {/* Left: Controls */}
                <div className="lg:col-span-2 space-y-4">
                  {/* Upload */}
                  <div className="glass border border-white/10 rounded-xl p-5">
                    <h3 className="text-xs text-zinc-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                      <Upload className="w-3 h-3" /> Upload Your Design
                    </h3>
                    <input type="file" ref={fileInputRef} accept="image/*" onChange={handleUpload} className="hidden" />
                    <Button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      variant="outline"
                      className="w-full border-dashed border-white/20 h-20 flex flex-col gap-1"
                      data-testid="upload-design-btn"
                    >
                      {uploading ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <>
                          <Upload className="w-5 h-5 text-zinc-400" />
                          <span className="text-xs text-zinc-400">Click to upload (PNG, JPG, max 10MB)</span>
                        </>
                      )}
                    </Button>
                  </div>

                  {/* OR Divider */}
                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-px bg-white/10" />
                    <span className="text-xs text-zinc-500 uppercase">or generate with AI</span>
                    <div className="flex-1 h-px bg-white/10" />
                  </div>

                  {/* Style Template */}
                  <div className="glass border border-white/10 rounded-xl p-5">
                    <h3 className="text-xs text-zinc-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                      <Sparkles className="w-3 h-3" /> Style
                    </h3>
                    <div className="flex flex-wrap gap-1.5">
                      {STYLE_TEMPLATES.map(t => (
                        <button
                          key={t.id}
                          onClick={() => setSelectedTemplate(t.id)}
                          className={`px-3 py-1.5 rounded-lg text-xs transition ${
                            selectedTemplate === t.id
                              ? "bg-neon-magenta/20 text-neon-magenta border border-neon-magenta/30"
                              : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700 border border-transparent"
                          }`}
                          data-testid={`style-${t.id || "custom"}`}
                        >
                          {t.name}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Prompt */}
                  <div className="glass border border-white/10 rounded-xl p-5">
                    <h3 className="text-xs text-zinc-400 uppercase tracking-wider mb-3">Describe Your Design</h3>
                    <Textarea
                      value={prompt}
                      onChange={e => setPrompt(e.target.value)}
                      placeholder={`e.g., "neon samurai with cherry blossoms" or "turbo snail with flames"`}
                      className="bg-zinc-800 border-white/10 min-h-[80px] mb-3"
                      data-testid="design-prompt-input"
                    />
                    <Button
                      onClick={handleGenerate}
                      disabled={generating}
                      className="w-full bg-gradient-to-r from-neon-magenta to-purple-500 text-white font-bold uppercase tracking-wider"
                      data-testid="generate-design-btn"
                    >
                      {generating ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Generating (~30s)...
                        </>
                      ) : (
                        <>
                          <Wand2 className="w-4 h-4 mr-2" />
                          Generate with AI
                        </>
                      )}
                    </Button>
                  </div>
                </div>

                {/* Right: Design Gallery */}
                <div className="lg:col-span-3">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xs text-zinc-400 uppercase tracking-wider">
                      My Designs {designs.length > 0 && `(${designs.length})`}
                    </h3>
                    {selectedDesign && (
                      <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-xs">
                        <Check className="w-3 h-3 mr-1" /> Selected
                      </Badge>
                    )}
                  </div>

                  {designs.length === 0 ? (
                    <div className="glass border border-white/10 rounded-xl flex flex-col items-center justify-center py-20">
                      <Palette className="w-12 h-12 text-zinc-600 mb-3" />
                      <p className="text-zinc-500 text-sm">No designs yet.</p>
                      <p className="text-zinc-600 text-xs mt-1">Upload an image or generate one with AI above.</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-[500px] overflow-y-auto pr-1">
                      {designs.map(d => (
                        <motion.div
                          key={d.design_id}
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          className={`relative rounded-xl overflow-hidden cursor-pointer group border-2 transition-all ${
                            selectedDesign?.design_id === d.design_id
                              ? "border-neon-cyan shadow-lg shadow-neon-cyan/20"
                              : "border-transparent hover:border-white/20"
                          }`}
                          onClick={() => setSelectedDesign(d)}
                          data-testid={`design-card-${d.design_id}`}
                        >
                          {d.image_base64 && (
                            <div className="aspect-square bg-zinc-900">
                              <img
                                src={`data:image/png;base64,${d.image_base64}`}
                                alt={d.prompt || "Design"}
                                className="w-full h-full object-cover"
                              />
                            </div>
                          )}
                          {selectedDesign?.design_id === d.design_id && (
                            <div className="absolute top-2 left-2 w-6 h-6 bg-neon-cyan rounded-full flex items-center justify-center">
                              <Check className="w-4 h-4 text-black" />
                            </div>
                          )}
                          <button
                            onClick={(e) => { e.stopPropagation(); deleteDesign(d.design_id); }}
                            className="absolute top-2 right-2 w-6 h-6 bg-black/60 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition text-red-400"
                            data-testid={`delete-design-${d.design_id}`}
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                          <div className="p-2 bg-black/40">
                            <p className="text-[10px] text-zinc-400 truncate">{d.prompt || d.source === "upload" ? "Uploaded" : "AI Generated"}</p>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  )}

                  {/* Next button */}
                  {selectedDesign && (
                    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-4">
                      <Button
                        onClick={() => setStep(2)}
                        className="w-full bg-neon-cyan text-black font-bold uppercase tracking-wider"
                        data-testid="next-to-preview-btn"
                      >
                        Next: Preview & Order <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </motion.div>
                  )}
                </div>
              </div>
            </motion.div>
          )}

          {/* STEP 2: Preview & Order */}
          {step === 2 && selectedDesign && (
            <motion.div key="step2" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }}>
              {!createdProduct ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Left: Preview */}
                  <div>
                    <h2 className="font-heading text-base sm:text-lg uppercase tracking-wide text-zinc-300 mb-4">Preview</h2>
                    <div className="relative rounded-xl overflow-hidden bg-zinc-900 border border-white/10">
                      {/* Product mockup with design overlay */}
                      <div className="aspect-square flex items-center justify-center p-8" style={{ background: `radial-gradient(circle, #1a1a2e 0%, #0a0a0a 100%)` }}>
                        <div className="relative w-full h-full flex items-center justify-center">
                          {/* Product silhouette */}
                          <div className={`relative ${productType === "phone-case" ? "w-40 h-72" : productType === "mug" ? "w-52 h-44" : productType === "sticker" ? "w-48 h-48" : productType === "poster" ? "w-52 h-68" : "w-64 h-72"}`}>
                            {/* Design image centered on product */}
                            {selectedDesign.image_base64 && (
                              <img
                                src={`data:image/png;base64,${selectedDesign.image_base64}`}
                                alt="Design preview"
                                className="w-full h-full object-contain rounded-lg"
                                data-testid="design-preview-image"
                              />
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="absolute bottom-3 right-3">
                        <Badge className="bg-black/60 text-zinc-300 border-0 text-[10px]">
                          {PRODUCT_TYPES.find(p => p.id === productType)?.label}
                        </Badge>
                      </div>
                    </div>
                  </div>

                  {/* Right: Configure & Order */}
                  <div className="space-y-5">
                    <h2 className="font-heading text-base sm:text-lg uppercase tracking-wide text-zinc-300">Configure Your Product</h2>

                    {/* Name */}
                    <div className="glass border border-white/10 rounded-xl p-5">
                      <label className="text-xs text-zinc-500 uppercase tracking-wide block mb-2">Product Name</label>
                      <input
                        type="text"
                        value={customName}
                        onChange={e => setCustomName(e.target.value)}
                        placeholder="e.g., My Neon Samurai Hoodie"
                        className="w-full bg-zinc-800 border border-white/10 rounded-lg px-4 py-2.5 text-sm focus:border-neon-cyan/50 focus:outline-none"
                        data-testid="custom-name-input"
                      />
                    </div>

                    {/* Size & Color */}
                    {currentProductInfo && (
                      <div className="glass border border-white/10 rounded-xl p-5 space-y-4">
                        {currentProductInfo.sizes?.length > 0 && (
                          <div>
                            <label className="text-xs text-zinc-500 uppercase tracking-wide block mb-2">Size</label>
                            <div className="flex flex-wrap gap-2">
                              {currentProductInfo.sizes.map(s => (
                                <button
                                  key={s}
                                  onClick={() => setSelectedSize(s)}
                                  className={`px-4 py-2 rounded-lg text-sm transition border ${
                                    selectedSize === s
                                      ? "bg-neon-cyan/20 text-neon-cyan border-neon-cyan/30"
                                      : "bg-zinc-800 text-zinc-400 border-white/10 hover:border-white/20"
                                  }`}
                                  data-testid={`custom-size-${s}`}
                                >
                                  {s}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                        {currentProductInfo.colors?.length > 0 && (
                          <div>
                            <label className="text-xs text-zinc-500 uppercase tracking-wide block mb-2">Color</label>
                            <div className="flex flex-wrap gap-2">
                              {currentProductInfo.colors.map(c => (
                                <button
                                  key={c}
                                  onClick={() => setSelectedColor(c)}
                                  className={`px-4 py-2 rounded-lg text-sm transition border ${
                                    selectedColor === c
                                      ? "bg-neon-cyan/20 text-neon-cyan border-neon-cyan/30"
                                      : "bg-zinc-800 text-zinc-400 border-white/10 hover:border-white/20"
                                  }`}
                                  data-testid={`custom-color-${c}`}
                                >
                                  {c}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Price */}
                    <div className="glass border border-white/10 rounded-xl p-5">
                      <div className="flex justify-between items-center">
                        <span className="text-zinc-400 text-sm">Total</span>
                        <span className="text-3xl font-mono text-neon-magenta font-bold" data-testid="custom-total-price">
                          ${currentProductInfo?.custom_price?.toFixed(2) || "0.00"}
                        </span>
                      </div>
                      <p className="text-xs text-zinc-500 mt-2">Includes print, production & worldwide shipping setup</p>
                    </div>

                    {/* Create button */}
                    <Button
                      onClick={handleCreateProduct}
                      disabled={creating || !customName.trim()}
                      className="w-full h-14 bg-neon-magenta text-black font-bold uppercase tracking-wider text-base"
                      data-testid="create-custom-product-btn"
                    >
                      {creating ? (
                        <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Creating...</>
                      ) : (
                        <><Sparkles className="w-5 h-5 mr-2" /> Create Custom Product</>
                      )}
                    </Button>
                  </div>
                </div>
              ) : (
                /* Success state */
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="max-w-lg mx-auto text-center"
                  data-testid="custom-product-success"
                >
                  <div className="glass border border-green-500/20 rounded-xl p-10">
                    <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                      <Check className="w-8 h-8 text-green-400" />
                    </div>
                    <h2 className="font-heading text-2xl uppercase mb-2">Product Created!</h2>
                    <p className="text-zinc-400 text-sm mb-6">{createdProduct.name} is ready to order.</p>
                    <p className="text-3xl font-mono text-neon-magenta font-bold mb-8">${createdProduct.price?.toFixed(2)}</p>

                    <div className="flex flex-col gap-3">
                      <Button
                        onClick={handleAddToCart}
                        className="w-full h-12 bg-neon-magenta text-black font-bold uppercase"
                        data-testid="add-custom-to-cart-btn"
                      >
                        <ShoppingCart className="w-5 h-5 mr-2" /> Add to Cart
                      </Button>
                      <Button
                        onClick={() => navigate(`/merch/${createdProduct.merch_id}`)}
                        variant="outline"
                        className="w-full border-white/20"
                        data-testid="view-custom-product-btn"
                      >
                        View Product Page
                      </Button>
                      <Button
                        onClick={() => { setCreatedProduct(null); setSelectedDesign(null); setStep(0); setCustomName(""); }}
                        variant="ghost"
                        className="text-zinc-400"
                      >
                        Design Another
                      </Button>
                    </div>
                  </div>
                </motion.div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
